import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  FormControl,
  FormLabel,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import MicIcon from "@mui/icons-material/Mic";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import SaveIcon from "@mui/icons-material/Save";
import {
  AudioConfig,
  CancellationReason,
  ResultReason,
  SpeechConfig,
  SpeechRecognizer,
} from "microsoft-cognitiveservices-speech-sdk";

import Page from "../../../../components/Pages/Page";
import PageHeader from "../../../../components/PageHeader";
import { useWorkspaceContext } from "../../../../providers/WorkspaceProvider";
import { useTranscripts } from "../../hooks/useTranscripts";
import { TranscriptFolder } from "../../types/transcripts";
import {
  SpeechTokenRefreshScheduler,
} from "./speechTokenRefresh";

const LANGUAGES = [
  { value: "en-US", label: "English (US)" },
  { value: "en-GB", label: "English (UK)" },
  { value: "sv-SE", label: "Swedish" },
  { value: "de-DE", label: "German" },
];

type CaptureState = "idle" | "starting" | "listening" | "paused" | "stopping" | "saved" | "error";

const SESSION_EXPIRED_MESSAGE = "Your session has expired. Reload to continue.";

// Named stages so a failure message can say which step failed rather than
// collapsing "create the transcript" and "request the speech token" into one
// unattributable sentence (#3871 item 3).
const STAGE = {
  create: "create the transcript",
  speechToken: "request the speech token",
  microphone: "open the microphone",
  recognition: "start speech recognition",
} as const;

type RecordingStage = (typeof STAGE)[keyof typeof STAGE];

class RecordingStageError extends Error {
  readonly stage: RecordingStage;
  readonly reason: unknown;

  constructor(stage: RecordingStage, reason: unknown) {
    super(`Failed to ${stage}`);
    this.name = "RecordingStageError";
    this.stage = stage;
    this.reason = reason;
  }
}

const runStage = async <T,>(stage: RecordingStage, op: () => Promise<T>): Promise<T> => {
  try {
    return await op();
  } catch (err) {
    throw new RecordingStageError(stage, err);
  }
};

const getErrorMessage = (err: unknown, fallback: string) => {
  const response = (err as { response?: { detail?: unknown } } | null)?.response;
  if (typeof response?.detail === "string" && response.detail.trim()) {
    return response.detail;
  }
  return err instanceof Error && err.message ? err.message : fallback;
};

const getStatus = (err: unknown): number | undefined => {
  const status = (err as { status?: unknown } | null)?.status;
  return typeof status === "number" ? status : undefined;
};

// A 401 here has already survived useAuthFetch's force-refresh + single retry, so
// it is a genuinely dead session rather than a transient token miss (#3871 item 2).
const describeFailure = (
  err: unknown,
  fallback: string,
): { message: string; canReload: boolean } => {
  const stage = err instanceof RecordingStageError ? err.stage : null;
  const reason = err instanceof RecordingStageError ? err.reason : err;
  const status = getStatus(reason);

  if (status === 401) {
    return { message: SESSION_EXPIRED_MESSAGE, canReload: true };
  }

  const detail = getErrorMessage(reason, fallback);
  const withCode = status ? `${detail} (HTTP ${status})` : detail;
  return {
    message: stage ? `Could not ${stage}. ${withCode}` : withCode,
    canReload: false,
  };
};

const StartTranscript: React.FC = () => {
  const navigate = useNavigate();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";
  const {
    appendSegment,
    completeTranscript,
    createTranscript,
    deleteTranscript,
    getSpeechToken,
    listFolders,
  } = useTranscripts();

  const [language, setLanguage] = useState("en-US");
  const [folderId, setFolderId] = useState<number | null>(null);
  const [folders, setFolders] = useState<TranscriptFolder[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [state, setState] = useState<CaptureState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [errorCanReload, setErrorCanReload] = useState(false);
  const [transcriptId, setTranscriptId] = useState<number | null>(null);
  const [interimText, setInterimText] = useState("");
  const [finalSegments, setFinalSegments] = useState<string[]>([]);
  const [startedAt, setStartedAt] = useState<number | null>(null);

  const recognizerRef = useRef<SpeechRecognizer | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const sequenceRef = useRef(0);
  const transcriptIdRef = useRef<number | null>(null);
  const tokenRefreshSchedulerRef = useRef<{ stop: () => void } | null>(null);

  const fullTranscript = useMemo(
    () => [...finalSegments, interimText].filter(Boolean).join("\n").trim(),
    [finalSegments, interimText],
  );
  const folderPathById = useMemo(() => {
    const folderById = new Map(folders.map((folder) => [folder.folder_id, folder]));
    const resolvePath = (folder: TranscriptFolder, seen = new Set<number>()): string => {
      if (seen.has(folder.folder_id)) return folder.name;
      seen.add(folder.folder_id);
      if (!folder.parent_folder_id) return folder.name;
      const parent = folderById.get(folder.parent_folder_id);
      return parent ? `${resolvePath(parent, seen)} / ${folder.name}` : folder.name;
    };
    return new Map(folders.map((folder) => [folder.folder_id, resolvePath(folder)]));
  }, [folders]);
  const isStarting = state === "starting";
  const isRecording = state === "listening";

  const clearError = useCallback(() => {
    setError(null);
    setErrorCanReload(false);
  }, []);

  const showFailure = useCallback((err: unknown, fallback: string) => {
    const { message, canReload } = describeFailure(err, fallback);
    setError(message);
    setErrorCanReload(canReload);
  }, []);

  useEffect(() => {
    if (!workspaceId) return;
    void listFolders(workspaceId).then(setFolders).catch(() => setFolders([]));
  }, [listFolders, workspaceId]);

  const loadAudioDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      setAudioDevices(devices.filter((device) => device.kind === "audioinput"));
    } catch {
      setAudioDevices([]);
    }
  }, []);

  useEffect(() => {
    void loadAudioDevices();
  }, [loadAudioDevices]);

  const cleanup = useCallback(() => {
    tokenRefreshSchedulerRef.current?.stop();
    tokenRefreshSchedulerRef.current = null;
    const recognizer = recognizerRef.current;
    recognizerRef.current = null;
    if (recognizer) {
      try {
        recognizer.stopContinuousRecognitionAsync(
          () => recognizer.close(),
          () => recognizer.close(),
        );
      } catch {
        recognizer.close();
      }
    }
    const stream = mediaStreamRef.current;
    mediaStreamRef.current = null;
    stream?.getTracks().forEach((track) => track.stop());
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const generatedTitle = useCallback(
    () =>
      `Recording ${new Date().toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })}`,
    [],
  );

  const startRecognition = async (currentTranscriptId: number) => {
    const token = await runStage(STAGE.speechToken, () =>
      getSpeechToken(workspaceId, currentTranscriptId),
    );
    const tokenReceivedAtMs = Date.now();
    const audio: MediaTrackConstraints = { sampleRate: 16000, channelCount: 1 };
    if (selectedDeviceId) {
      audio.deviceId = { exact: selectedDeviceId };
    }
    const mediaStream = await runStage(STAGE.microphone, () =>
      navigator.mediaDevices.getUserMedia({ audio }),
    );
    mediaStreamRef.current = mediaStream;
    void loadAudioDevices();

    const speechConfig = SpeechConfig.fromAuthorizationToken(token.token, token.region);
    speechConfig.speechRecognitionLanguage = language;
    const audioConfig = AudioConfig.fromStreamInput(mediaStream);
    const recognizer = new SpeechRecognizer(speechConfig, audioConfig);
    recognizerRef.current = recognizer;

    recognizer.recognizing = (_sender, event) => {
      setInterimText(event.result?.text ?? "");
    };
    recognizer.recognized = (_sender, event) => {
      if (event.result?.reason !== ResultReason.RecognizedSpeech) return;
      const text = (event.result.text ?? "").trim();
      if (!text) return;
      const sequenceIndex = sequenceRef.current++;
      setInterimText("");
      setFinalSegments((current) => [...current, text]);
      void appendSegment(workspaceId, currentTranscriptId, {
        client_segment_id: `${currentTranscriptId}-${sequenceIndex}`,
        sequence_index: sequenceIndex,
        text,
        is_final: true,
      }).catch((err) => {
        console.error("[transcripts] appendSegment failed", err);
        const status = getStatus(err);
        setError(
          status === 401
            ? SESSION_EXPIRED_MESSAGE
            : `A transcript segment could not be saved yet${status ? ` (HTTP ${status})` : ""}. Stop and save will persist the visible text.`,
        );
        setErrorCanReload(status === 401);
      });
    };
    recognizer.canceled = (_sender, event) => {
      if (event.reason === CancellationReason.Error) {
        console.error("[transcripts] speech recognition canceled", {
          errorCode: event.errorCode,
          errorDetails: event.errorDetails,
          reason: event.reason,
        });
        const code = event.errorCode !== undefined ? ` (code ${event.errorCode})` : "";
        const details = event.errorDetails ? ` ${event.errorDetails}` : "";
        setError(`Speech recognition stopped unexpectedly${code}.${details}`);
        setErrorCanReload(false);
        setState("error");
        cleanup();
      }
    };

    const tokenRefreshScheduler = new SpeechTokenRefreshScheduler({
      refreshToken: () => getSpeechToken(workspaceId, currentTranscriptId),
      isActive: () => recognizerRef.current !== null,
      onToken: (freshToken) => {
        if (recognizerRef.current) {
          recognizerRef.current.authorizationToken = freshToken.token;
        }
      },
      onExpiryFallback: () => {
        console.warn(
          "[transcripts] speech token expiry was invalid or elapsed; using 6-minute refresh fallback",
        );
      },
      onRefreshFailure: (err) => {
        console.error("[transcripts] speech token refresh failed", err);
        const status = getStatus(err);
        setError(
          status === 401
            ? SESSION_EXPIRED_MESSAGE
            : `Could not refresh the speech token${status ? ` (HTTP ${status})` : ""}. Stop and save the transcript before continuing.`,
        );
        setErrorCanReload(status === 401);
      },
      onRetriesExhausted: () => {
        console.error("[transcripts] speech token refresh retries exhausted");
      },
    });
    tokenRefreshSchedulerRef.current = tokenRefreshScheduler;

    await new Promise<void>((resolve, reject) => {
      recognizer.startContinuousRecognitionAsync(
        () => {
          setStartedAt((current) => current ?? Date.now());
          tokenRefreshScheduler.start(token.expires_in, tokenReceivedAtMs);
          setState("listening");
          resolve();
        },
        (sdkError) => {
          // The SDK passes its error string as the first argument; the previous
          // callback took no parameter and dropped it entirely (#3871 item 1).
          console.error("[transcripts] startContinuousRecognitionAsync failed", sdkError);
          cleanup();
          reject(
            new RecordingStageError(
              STAGE.recognition,
              new Error(
                typeof sdkError === "string" && sdkError.trim()
                  ? sdkError
                  : "The speech SDK did not report a reason.",
              ),
            ),
          );
        },
      );
    });
  };

  const start = async () => {
    if (!workspaceId) return;
    setState("starting");
    clearError();
    setFinalSegments([]);
    setInterimText("");
    setStartedAt(null);
    sequenceRef.current = 0;

    let createdTranscriptId: number | null = null;
    try {
      const transcript = await runStage(STAGE.create, () =>
        createTranscript(workspaceId, {
          title: generatedTitle(),
          language,
          folder_id: folderId,
        }),
      );
      createdTranscriptId = transcript.transcript_id;
      setTranscriptId(transcript.transcript_id);
      transcriptIdRef.current = transcript.transcript_id;
      await startRecognition(transcript.transcript_id);
    } catch (err) {
      console.error("[transcripts] start recording failed", err);
      cleanup();
      if (createdTranscriptId != null) {
        try {
          await deleteTranscript(workspaceId, createdTranscriptId);
        } catch (cleanupErr) {
          // Best effort cleanup: the user-visible failure remains the original
          // start failure, but the cleanup failure is still worth logging.
          console.error("[transcripts] rollback of the created transcript failed", cleanupErr);
        }
        setTranscriptId(null);
        transcriptIdRef.current = null;
      }
      showFailure(err, "Could not start transcription.");
      setState("error");
    }
  };

  const resume = async () => {
    const currentTranscriptId = transcriptIdRef.current;
    if (!workspaceId || !currentTranscriptId) return;
    setState("starting");
    clearError();
    try {
      await startRecognition(currentTranscriptId);
    } catch (err) {
      console.error("[transcripts] resume recording failed", err);
      cleanup();
      showFailure(err, "Could not resume transcription.");
      setState("error");
    }
  };

  const pause = () => {
    if (state !== "listening") return;
    cleanup();
    setState("paused");
  };

  const stopAndSave = async () => {
    const currentTranscriptId = transcriptIdRef.current;
    if (!workspaceId || !currentTranscriptId) return;
    setState("stopping");
    cleanup();
    const text = fullTranscript;
    const durationSeconds = startedAt ? Math.max(1, Math.round((Date.now() - startedAt) / 1000)) : undefined;
    try {
      const completed = await completeTranscript(workspaceId, currentTranscriptId, {
        transcript_text: text,
        duration_seconds: durationSeconds,
      });
      setState("saved");
      navigate(`/workspace/${workspaceId}/meridian/transcripts/${completed.transcript_id}`);
    } catch (err) {
      console.error("[transcripts] save transcript failed", err);
      showFailure(err, "Could not save the transcript.");
      setState("error");
    }
  };

  return (
    <Page>
      <PageHeader>
        <Box>
          <Typography level="h1">Start recording</Typography>
          <Typography level="body-sm" color="neutral">
            Transcribe a live conversation from this device microphone. Audio is not stored by Meridian.
          </Typography>
        </Box>
      </PageHeader>
      {error && (
        <Alert
          color="danger"
          sx={{ mb: 2 }}
          endDecorator={
            errorCanReload ? (
              <Button size="sm" variant="solid" color="danger" onClick={() => window.location.reload()}>
                Reload
              </Button>
            ) : undefined
          }
        >
          {error}
        </Alert>
      )}
      <Stack spacing={2}>
        <Card variant="outlined">
          <Stack spacing={2}>
            <Typography level="body-sm" color="neutral">
              Meridian will generate a title when the transcript is saved. You can rename it from the repository tree or detail view.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl sx={{ minWidth: 180 }}>
                <FormLabel>Language</FormLabel>
                <Select value={language} onChange={(_event, value) => setLanguage(value ?? "en-US")} disabled={state === "listening"}>
                  {LANGUAGES.map((item) => <Option key={item.value} value={item.value}>{item.label}</Option>)}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 260 }}>
                <FormLabel>Microphone</FormLabel>
                <Select
                  value={selectedDeviceId || "default"}
                  onChange={(_event, value) => setSelectedDeviceId(value === "default" ? "" : value ?? "")}
                  disabled={state === "listening" || state === "starting"}
                >
                  <Option value="default">Default microphone</Option>
                  {audioDevices.map((device, index) => (
                    <Option key={device.deviceId || `mic-${index}`} value={device.deviceId}>
                      {device.label || `Microphone ${index + 1}`}
                    </Option>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 220 }}>
                <FormLabel>Folder</FormLabel>
                <Select
                  value={folderId == null ? "none" : String(folderId)}
                  onChange={(_event, value) => setFolderId(value === "none" ? null : Number(value))}
                  disabled={state === "listening"}
                >
                  <Option value="none">No folder</Option>
                  {folders.map((folder) => (
                    <Option key={folder.folder_id} value={String(folder.folder_id)}>
                      {folderPathById.get(folder.folder_id) ?? folder.name}
                    </Option>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-start" alignItems="center" flexWrap="wrap">
              {!transcriptId ? (
                <Button startDecorator={<MicIcon />} onClick={start} disabled={isStarting} loading={isStarting}>
                  Start recording
                </Button>
              ) : isStarting ? (
                <Button startDecorator={<PlayArrowIcon />} disabled loading>
                  Starting…
                </Button>
              ) : isRecording ? (
                <Button color="danger" startDecorator={<StopIcon />} onClick={stopAndSave}>
                  Stop and save
                </Button>
              ) : state === "saved" ? (
                <Button variant="outlined" disabled>
                  Saved
                </Button>
              ) : (
                <Button startDecorator={<PlayArrowIcon />} onClick={resume}>
                  Resume recording
                </Button>
              )}
              <Button
                variant="outlined"
                color="neutral"
                startDecorator={<PauseIcon />}
                onClick={pause}
                disabled={!isRecording}
              >
                Pause
              </Button>
              {state !== "listening" && transcriptId && (
                <Button variant="outlined" startDecorator={<SaveIcon />} onClick={stopAndSave} disabled={!fullTranscript}>
                  Save transcript
                </Button>
              )}
            </Stack>
          </Stack>
        </Card>

        <Card variant="outlined">
          <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography level="title-md">Live transcript</Typography>
              <Typography level="body-sm" color={state === "listening" ? "success" : "neutral"}>
                {state === "listening" ? "Listening" : "Not listening"}
              </Typography>
            </Stack>
            <Textarea
              minRows={14}
              value={fullTranscript}
              readOnly
              placeholder="Recognized text will appear here as you speak."
              sx={{ fontFamily: "monospace" }}
            />
            <Typography level="body-xs" color="neutral">
              Browser microphone audio is streamed directly to Azure Speech for recognition. Meridian stores text segments only.
            </Typography>
          </Stack>
        </Card>
      </Stack>
    </Page>
  );
};

export default StartTranscript;
